import React, { useState } from "react";
import { motion } from "framer-motion";

import NavBar from "../components/Navbar5";
import JobApplicationModal from "../components_carrer/JobApplicationModal";
import styles from "../styles_career/CareerPage.module.css";
import {
  FaCode,
  FaRocket,
  FaUsers,
  FaLaptopCode,
  FaBrain,
  FaChartLine,
  FaGraduationCap,
  FaHandshake,
  FaAward,
} from "react-icons/fa";

import Footer from "../components/Footer1";
import SEO from "../components/SEO";

const careerPageSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Careers at VishvaConnect Technologies LLP",
  url: "https://www.vishvaconnect.com/career",
  description:
    "Explore software engineering, AI and UX/UI career opportunities at VishvaConnect Technologies LLP.",
  about: {
    "@type": "Organization",
    name: "VISHVACONNECT TECHNOLOGIES LLP",
    url: "https://www.vishvaconnect.com/",
  },
  mainEntity: {
    "@type": "ItemList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Senior Software Engineer",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "AI/ML Research Scientist",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "UX/UI Designer",
      },
    ],
  },
};

// Job Openings Component
const JobOpenings = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedJob, setSelectedJob] = useState(null);

  const jobCategories = [
    "All",
    "Engineering",
    "Design",
    "Product",
    "Sales",
    "Marketing",
  ];

  const jobOpenings = [
    {
      title: "Senior Software Engineer",
      category: "Engineering",
      location: "Remote",
      description:
        "Design and develop scalable, high-performance software solutions using cutting-edge technologies.",
      requirements: [
        "5+ years of software development experience",
        "Expertise in React, Node.js, and cloud technologies",
        "Strong problem-solving and communication skills",
      ],
    },
    {
      title: "AI/ML Research Scientist",
      category: "Engineering",
      location: "Hybrid",
      description:
        "Lead innovative AI research and develop advanced machine learning models.",
      requirements: [
        "PhD or Master's in Computer Science, AI, or related field",
        "Proven track record in machine learning research",
        "Experience with TensorFlow and PyTorch",
      ],
    },
    {
      title: "UX/UI Designer",
      category: "Design",
      location: "On-site",
      description:
        "Create intuitive and visually stunning user experiences for our digital products.",
      requirements: [
        "3+ years of UX/UI design experience",
        "Proficiency in Figma and design systems",
        "Strong portfolio showcasing innovative design solutions",
      ],
    },
  ];

  const filteredJobs =
    activeCategory === "All"
      ? jobOpenings
      : jobOpenings.filter((job) => job.category === activeCategory);

  const handleApplyNow = (job) => {
    setSelectedJob(job);
  };

  const handleCloseModal = () => {
    setSelectedJob(null);
  };

  return (
    <section id="openings" className={styles.jobOpeningsSection}>
      <h2>Current Openings</h2>
      <div className={styles.jobCategoryFilter}>
        {jobCategories.map((category) => (
          <button
            key={category}
            className={`${styles.categoryButton} ${activeCategory === category ? styles.activeCategory : ""}`}
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className={styles.jobGrid}>
        {filteredJobs.map((job, index) => (
          <motion.div
            key={index}
            className={styles.jobCard}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.5,
              delay: index * 0.2,
            }}
          >
            <div className={styles.jobCardHeader}>
              <h3>{job.title}</h3>
              <span className={styles.jobLocation}>{job.location}</span>
            </div>
            <p className={styles.jobDescription}>{job.description}</p>
            <div className={styles.jobRequirements}>
              <h4>Key Requirements:</h4>
              <ul>
                {job.requirements.map((req, reqIndex) => (
                  <li key={reqIndex}>{req}</li>
                ))}
              </ul>
            </div>
            <button
              className={styles.applyButton}
              onClick={() => handleApplyNow(job)}
            >
              Apply Now
            </button>
          </motion.div>
        ))}
      </div>

      {selectedJob && (
        <JobApplicationModal
          isOpen={!!selectedJob}
          onClose={handleCloseModal}
          jobTitle={selectedJob.title}
        />
      )}
    </section>
  );
};

// Why Join Us Component
const WhyJoinUs = () => {
  const benefitPoints = [
    {
      icon: FaRocket,
      title: "Innovation Culture",
      description:
        "Be at the forefront of technological innovation and work on groundbreaking projects.",
      color: "#00A4E4",
    },
    {
      icon: FaUsers,
      title: "Collaborative Environment",
      description:
        "Join a diverse, inclusive team that values creativity, learning, and mutual growth.",
      color: "#4CAF50",
    },
    {
      icon: FaGraduationCap,
      title: "Continuous Learning",
      description:
        "Access to professional development programs, workshops, and cutting-edge training.",
      color: "#FF9800",
    },
    {
      icon: FaHandshake,
      title: "Competitive Benefits",
      description:
        "Comprehensive health insurance, flexible work arrangements, and competitive compensation.",
      color: "#9C27B0",
    },
  ];

  return (
    <section className={styles.whyJoinUsSection}>
      <h2>Why Join VishvaConnect</h2>
      <div className={styles.benefitsGrid}>
        {benefitPoints.map((benefit, index) => (
          <motion.div
            key={index}
            className={styles.benefitCard}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: index * 0.2,
            }}
          >
            <div
              className={styles.benefitIcon}
              style={{
                backgroundColor: `${benefit.color}20`,
                color: benefit.color,
              }}
            >
              <benefit.icon />
            </div>
            <h3>{benefit.title}</h3>
            <p>{benefit.description}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// Career Hero Section
const CareerHero = () => {
  return (
    <section className={styles.careerHeroSection}>
      <div className={styles.heroContent}>
        <motion.h1
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Build Your Career at VishvaConnect
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          We're not just building technology. We're creating opportunities for
          passionate innovators to transform the world.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <a href="#openings" className={styles.exploreButton}>
            Explore Opportunities
          </a>
        </motion.div>
      </div>
    </section>
  );
};

function CareerPage() {
  return (
    <div className={styles.careerPageContainer}>
      <SEO
  title="Careers at VishvaConnect | Software, AI & Design Jobs"
  description="Explore career opportunities at VishvaConnect Technologies LLP, including software engineering, AI and machine learning, and UX/UI design roles."
  path="/career"
  schema={careerPageSchema}
/>
      <NavBar />
      <CareerHero />
      <div className={styles.careerPageContent}>
        <WhyJoinUs />
        <JobOpenings />
      </div>
      <Footer />
    </div>
  );
}

export default CareerPage;
